/**
 * MapConfigSummary Component
 *
 * Read-only summary of the active workspace's map configuration on the settings
 * page: country, center (reverse-geocoded place name + coordinate caption) and
 * the three zoom levels. Rendered as a flat section with an "Edit" action that
 * opens the full map configurator.
 *
 * @module pages/SettingsPage/components/parts/MapConfigSummary
 */

import { type ReactElement, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Group, Loader, Stack, Text } from '@mantine/core';

import { useI18n } from '@/hooks/useI18n';
import { useReverseGeocodeLabel } from '@/hooks/useReverseGeocodeLabel';
import { useGeoMarkupStore } from '@/store';
import { selectActiveWorkspaceMapSettings } from '@/store/selectors';
import { mapRoutes } from '@/routes/routes';
import { resolveMapSettings } from '@/utils/mapSettingsDefaults';
import { MAP_SETUP_COORDINATE_DECIMALS } from '@/constants';
import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import type { Coordinates } from '@/types';

import styles from './MapConfigSummary.module.css';

/** Placeholder glyph shown when a map setting has no value yet */
const EMPTY_VALUE_PLACEHOLDER = '—';

/** Tight gap (px) between the center place name and its coordinate caption */
const PLACE_COORDS_GAP = 2;

interface MapConfigSummaryProps {
  /** Short workspace id used to build the "edit map configuration" route. */
  workspaceShortId: string;
}

/** Format the center as a single `lat, lng` caption at the configured precision. */
function formatCoords(center: Coordinates): string {
  const decimals = MAP_SETUP_COORDINATE_DECIMALS;
  return `${center.lat.toFixed(decimals)}, ${center.lng.toFixed(decimals)}`;
}

export const MapConfigSummary = memo(function MapConfigSummary({
  workspaceShortId,
}: MapConfigSummaryProps): ReactElement {
  const { t } = useTranslation();
  const { currentLanguage } = useI18n();
  const navigate = useNavigate();
  const mapSettings = useGeoMarkupStore(selectActiveWorkspaceMapSettings);

  // Existing workspaces may be flagged configured with an empty settings object;
  // resolve effective values so the summary suggests defaults instead of blanks.
  const resolved = useMemo(
    () => resolveMapSettings(mapSettings),
    [mapSettings]
  );

  // Stabilize the center reference by its primitive coordinates so a background
  // refresh of the workspace slice (new object identity, same numbers) does not
  // re-fire the debounced reverse-geocode request.
  const centerLat = resolved.center?.lat;
  const centerLng = resolved.center?.lng;
  const center = useMemo<Coordinates | null>(
    () =>
      centerLat !== undefined && centerLng !== undefined
        ? { lat: centerLat, lng: centerLng }
        : null,
    [centerLat, centerLng]
  );

  // Best-effort reverse-geocoded place name for the configured center; falls
  // back to a placeholder and never blocks rendering.
  const { label: centerPlaceLabel, isLoading: isCenterPlaceLoading } =
    useReverseGeocodeLabel(center);

  // Localized country name derived from the persisted ISO 3166-1 code; falls
  // back to the raw code when the runtime cannot resolve a display name.
  const countryName = useMemo(() => {
    const code = resolved.countryCode;
    try {
      const displayNames = new Intl.DisplayNames([currentLanguage], {
        type: 'region',
      });
      return displayNames.of(code) ?? code;
    } catch {
      return code;
    }
  }, [resolved.countryCode, currentLanguage]);

  const zoomRows = useMemo(
    () => [
      {
        key: 'startZoom',
        label: t('mapSetup.zoom.start'),
        value: String(resolved.startZoom),
      },
      {
        key: 'minZoom',
        label: t('mapSetup.zoom.min'),
        value: String(resolved.minZoom),
      },
      {
        key: 'maxZoom',
        label: t('mapSetup.zoom.max'),
        value: String(resolved.maxZoom),
      },
    ],
    [resolved, t]
  );

  const handleEditMapConfig = useCallback(() => {
    if (!workspaceShortId) return;
    navigate(`${mapRoutes.mapConfig(workspaceShortId)}?mode=edit`);
  }, [workspaceShortId, navigate]);

  return (
    <>
      <hr className={styles.divider} />

      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Text fw={FONT_WEIGHT_SEMIBOLD}>{t('mapSetup.summary.title')}</Text>
          <Button variant="light" onClick={handleEditMapConfig}>
            {t('mapSetup.editButton')}
          </Button>
        </Group>

        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            {t('mapSetup.summary.country')}
          </Text>
          <Text size="sm">{countryName || EMPTY_VALUE_PLACEHOLDER}</Text>
        </Group>

        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text size="sm" c="dimmed">
            {t('mapSetup.summary.center')}
          </Text>
          {center ? (
            <Stack gap={PLACE_COORDS_GAP} className={styles.centerValue}>
              {isCenterPlaceLoading ? (
                <Loader size="xs" />
              ) : (
                <Text size="sm" ta="right">
                  {centerPlaceLabel}
                </Text>
              )}
              <Text size="xs" c="dimmed" className={styles.coords}>
                {formatCoords(center)}
              </Text>
            </Stack>
          ) : (
            <Text size="sm">{EMPTY_VALUE_PLACEHOLDER}</Text>
          )}
        </Group>

        {zoomRows.map((row) => (
          <Group key={row.key} justify="space-between" align="center">
            <Text size="sm" c="dimmed">
              {row.label}
            </Text>
            <Text size="sm">{row.value}</Text>
          </Group>
        ))}
      </Stack>
    </>
  );
});
